import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  GridView as GridViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import PaginationItem from '@mui/material/PaginationItem';
import {
  RiAddLine,
  RiMoreFill,
  RiGridLine,
  RiRefreshLine,
  RiEditLine,
  RiDeleteBinLine,
  RiPlayLine,
  RiTvLine,
  RiArrowUpDownLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiExpandUpDownLine,
  RiMore2Fill,
  RiStore2Fill,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from '@remixicon/react';
import TVCard from '../components/TVCard';
import PairingCodeModal from '../components/PairingCodeModal';
import { fetchTVs, fetchTVsFromAllBranches, createTV, removeTV, linkMediaToTV } from '../redux/slices/tvSlice';
import { generatePairingCode, addClientDataForParing, deleteTVBySerial, reloadTV, clearCacheTV } from '../services/firebaseService';
import TV from '../assets/images/tv.svg';
import { borderRadius, lineHeights, naturalScale, typography, colors, spacing } from '../theme/tokens';
import { formatDateTime, isOlderThan24Hours } from '../utils/helpers';
import ClearCache from '../assets/images/clear_cache.svg'

const TVManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const takeawayList = useSelector((state) => state.auth.takeawayList);
  const { tvs: rawTVs, loading, error } = useSelector((state) => state.tv);
  const [pairingModalOpen, setPairingModalOpen] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(null);
  const [selectedTVs, setSelectedTVs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');

  // Process TVs to determine online/offline status based on lastActive
  const [processedTVs, setProcessedTVs] = useState([]);

  useEffect(() => {
    if (rawTVs && rawTVs.length > 0) {
      const active = [];
      const inactive = [];

      rawTVs.forEach((tv) => {
        const isActive = !isOlderThan24Hours(tv?.lastActive);
        const tvWithStatus = {
          ...tv,
          isActive,
          status: isActive ? 'online' : 'offline', // Update status based on lastActive
        };

        if (isActive) {
          active.push(tvWithStatus);
        } else {
          inactive.push(tvWithStatus);
        }
      });

      // Sort: active TVs first, then inactive
      setProcessedTVs([...active, ...inactive]);
    } else {
      setProcessedTVs([]);
    }
  }, [rawTVs]);

  // Use processed TVs instead of raw TVs
  const tvs = processedTVs;

  // Extract branches from takeawayList
  const branches = takeawayList && takeawayList.length > 0
    ? takeawayList.map((takeaway) => ({
      id: takeaway.store_id || takeaway.id,
      name: takeaway.name || takeaway.branch_name || takeaway.license_key || 'Unknown Branch',
      value: takeaway.store_id || takeaway.id,
    }))
    : [];
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedTV, setSelectedTV] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const userId = 'demo-user'; // Replace with actual user ID

  // Fetch TVs from all branches on mount and when takeawayList changes
  useEffect(() => {
    if (takeawayList && takeawayList.length > 0) {
      const storeIds = takeawayList.map((takeaway) => takeaway.store_id).filter(Boolean);
      if (storeIds.length > 0) {
        dispatch(fetchTVsFromAllBranches(storeIds));
      }
    }
  }, [dispatch, takeawayList]);

  const handleAddTV = () => {
    const code = generatePairingCode();
    setGeneratedCode(code);
    setPairingModalOpen(true);
  };

  const handlePairTV = async (data) => {
    try {
      // Find the active store/branch from takeawayList using the selected branch value
      const selectedActiveStore =
        takeawayList?.find(
          (takeaway) =>
            (takeaway.license_key || takeaway.id) === data.branch
        ) || null;

      if (!selectedActiveStore) {
        alert('Selected branch not found. Please try again.');
        return;
      }

      // Build TV data using the provided structure
      const tvData = {
        name: `DMB${(tvs?.length || 0) + 1}`,
        storeId: selectedActiveStore?.store_id,
        store_name:
          selectedActiveStore?.name ||
          selectedActiveStore?.branch_name ||
          selectedActiveStore?.license_key,
        activeStoreLicenseKey:
          selectedActiveStore?.license_key || selectedActiveStore?.id,
        slides_updated_at: Date.now(),
        lastActive: Date.now(),
      };

      // Save pairing data under pairing/{code}
      await addClientDataForParing(data.pairingCode.toLowerCase(), tvData);

      setTimeout(() => {
        if (takeawayList && takeawayList.length > 0) {
          const storeIds = takeawayList
            .map((takeaway) => takeaway.store_id)
            .filter(Boolean);
          if (storeIds.length > 0) {
            dispatch(fetchTVsFromAllBranches(storeIds));
          }
        }
      }, 3000);

      setPairingModalOpen(false);
      setGeneratedCode(null);
    } catch (error) {
      console.error('Failed to pair TV:', error);
      alert(`Failed to pair TV: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteTV = async (tv) => {
    if (window.confirm('Do you want to delete this TV ?!')) {
      try {
        const serialNumber = tv.serialNumber || tv.id;
        await deleteTVBySerial(userId, serialNumber);
        // Refresh TVs after deletion
        if (takeawayList && takeawayList.length > 0) {
          const storeIds = takeawayList.map((takeaway) => takeaway.store_id).filter(Boolean);
          if (storeIds.length > 0) {
            dispatch(fetchTVsFromAllBranches(storeIds));
          }
        }
      } catch (error) {
        console.error('Failed to delete TV:', error);
        alert(`Failed to delete TV: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleRefresh = async (tv) => {
    if (window.confirm('Do you want to restart this TV ?!')) {
      try {
        const serialNumber = tv.serialNumber || tv.id;
        await reloadTV(serialNumber);
        alert('Restart command sent successfully!');
      } catch (error) {
        console.error('Failed to restart TV:', error);
        alert(`Failed to restart TV: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleClearCache = async (tv) => {
    if (window.confirm('Do you want to clear cache for this TV ?!')) {
      try {
        const serialNumber = tv.serialNumber || tv.id;
        await clearCacheTV(serialNumber);
        alert('Clear cache command sent successfully!');
      } catch (error) {
        console.error('Failed to clear cache:', error);
        alert(`Failed to clear cache: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedTVs(filteredTVs.map((tv) => tv.id));
    } else {
      setSelectedTVs([]);
    }
  };

  const handleSelectTV = (tvId) => {
    setSelectedTVs((prev) =>
      prev.includes(tvId) ? prev.filter((id) => id !== tvId) : [...prev, tvId]
    );
  };

  const handleActionClick = (event, tv) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedTV(tv);
  };

  const handleActionClose = () => {
    setActionMenuAnchor(null);
    setSelectedTV(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter TVs
  const filteredTVs = (tvs || []).filter((tv) => {
    const statusMatch = statusFilter === 'all' || tv.status === statusFilter;
    // Match branch by storeId (which should match license_key from takeawayList)
    const branchMatch = branchFilter === 'all' ||
      tv.storeId === branchFilter ||
      tv.branch === branchFilter ||
      tv.license_key === branchFilter;
    return statusMatch && branchMatch;
  });

  // Sort TVs
  const sortedTVs = [...filteredTVs].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue, bValue;

    if (sortColumn === 'name') {
      aValue = a.name.toLowerCase();
      bValue = b.name.toLowerCase();
    } else if (sortColumn === 'status') {
      aValue = a.status;
      bValue = b.status;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Branches are now from takeawayList (already defined above)

  // Paginate TVs
  const paginatedTVs = sortedTVs.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const totalPages = Math.ceil(sortedTVs.length / rowsPerPage);

  if (loading && tvs.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {tvs.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="h5" fontWeight={600} gutterBottom
            sx={{
              textAlign: 'center',
              lineHeight: lineHeights.headingMdNormal,
              fontFamily: typography.fontFamily,
              color: naturalScale[5],
              fontSize: '2.25rem',
              maxWidth: 550,
            }}>
            Bring your menu to life with Digi Solutions—let's go!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph
            sx={{
              textAlign: 'center',
              lineHeight: lineHeights.headingMdNormal,
              fontFamily: typography.fontFamily,
              color: naturalScale[50],
              fontSize: '1rem',
              maxWidth: 550,
              mb: 5,
            }}>
            Setting up your first screen is quick and easy—let's get started!
          </Typography>
          <Box
            component="img"
            src={TV}
            alt="No TVs"
            sx={{ maxWidth: 625, mb: 5, maxHeight: 280 }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <Button
            variant="contained"
            size="large"
            startIcon={<RiAddLine />}
            onClick={handleAddTV}
            sx={{
              borderRadius: borderRadius.xxxs,
            }}
          >
            Let's Pair Your Screen!
          </Button>
        </Box>
      ) : (
        <Box>

          {/* Header with filters */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              mb: 3,
              pr: 2,
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<RiAddLine size={24} />}
              onClick={handleAddTV}
              sx={{
                borderRadius: borderRadius.xxxs,
              }}
            >
              Add screen
            </Button>
          </Box>
          {/* Table */}
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: borderRadius.xxxs,
              boxShadow: 'none',
              border: `1px solid ${colors.neutral.gray200}`,
            }}
          >
            {/* Header with filters */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                bgcolor: naturalScale[150],
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                  All items
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredTVs.length} items
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {/* Status Filter */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: '5px' }}
                  >
                    <MenuItem value="all">Player Status</MenuItem>
                    <MenuItem value="online">Online</MenuItem>
                    <MenuItem value="offline">Offline</MenuItem>
                  </Select>
                </FormControl>

                {/* Branch Filter */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select
                    value={branchFilter}
                    onChange={(e) => setBranchFilter(e.target.value)}
                    displayEmpty
                    startAdornment={<RiStore2Fill size={24} style={{ marginRight: 8 }} color={'#4B4A57'} />}
                    sx={{ borderRadius: '5px' }}
                  >
                    <MenuItem value="all">All Branches</MenuItem>
                    {branches.map((branch) => (
                      <MenuItem key={branch.id || branch.value} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* View Toggle */}
                {/* <IconButton>
                  <RiGridLine size={20} />
                </IconButton> */}

                {/* Refresh */}
                <IconButton
                  onClick={() => {
                    if (takeawayList && takeawayList.length > 0) {
                      const storeIds = takeawayList.map((takeaway) => takeaway.store_id).filter(Boolean);
                      if (storeIds.length > 0) {
                        dispatch(fetchTVsFromAllBranches(storeIds));
                      }
                    }
                  }}
                  sx={{
                    borderRadius: '5px',
                    border: '1px solid #EFEFF0',
                    p: 1,
                  }}
                >
                  <RiRefreshLine size={20} />
                </IconButton>
              </Box>
            </Box>
            <Table>
              <TableHead sx={{ bgcolor: colors.neutral.gray50 }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedTVs.length > 0 && selectedTVs.length < filteredTVs.length
                      }
                      checked={
                        filteredTVs.length > 0 && selectedTVs.length === filteredTVs.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      onClick={() => handleSort('name')}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        Name
                      </Typography>
                      <RiExpandUpDownLine size={16} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      onClick={() => handleSort('status')}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        Player Status
                      </Typography>
                      <RiExpandUpDownLine size={16} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Branch
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Last Active
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight={600}>
                      Actions
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTVs.map((tv) => (
                  <TableRow
                    key={tv.id}
                    hover
                    selected={selectedTVs.includes(tv.id)}
                    onClick={() => navigate(`/tvs/${tv.id}`)}
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      cursor: 'pointer',
                    }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedTVs.includes(tv.id)}
                        onChange={() => handleSelectTV(tv.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {tv.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tv.status === 'online' ? 'Online' : 'Offline'}
                        size="small"
                        icon={
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: tv.status === 'online' ? colors.neutral.white : colors.neutral.black,
                            }}
                          />
                        }
                        sx={{
                          bgcolor: tv.status === 'online' ? colors.success.dark : '#EBEBED',
                          color: tv.status === 'online' ? colors.neutral.white : colors.neutral.black,
                          fontWeight: 600,
                          borderRadius: '5px',
                          '& .MuiChip-icon': {
                            ml: 1,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tv.store_name}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: '5px',
                          borderColor: colors.neutral.gray300,
                          borderWidth: tv.store_name ? 1 : 0,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tv?.lastActive ? formatDateTime(tv?.lastActive) : null}
                        size="small"
                        // variant="outlined"
                        sx={{
                          backgroundColor: colors.neutral.white,
                          // borderRadius: '5px',
                          // borderColor: colors.neutral.gray300,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleActionClick(e, tv)}
                      >
                        <RiMore2Fill size={24} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {/* Pagination */}
            <Box
              sx={{
                display: 'flex',
                bgcolor: naturalScale[150],
                // flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                // gap: 2,
                // mt: 3,
                // mb: 2,
                p: 2,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handleChangePage}
                color="primary"
                shape="rounded"
                size="medium"
                renderItem={(item) => (
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: () => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <RiArrowLeftSLine size={16} />
                          <Typography variant="body2" sx={{ color: '#575E67' }}>
                            Previous
                          </Typography>
                        </Box>
                      ),
                      next: () => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ color: '#575E67' }}>
                            Next
                          </Typography>
                          <RiArrowRightSLine size={16} />
                        </Box>
                      ),
                    }}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: '#EFEFF0',
                        border: '1px solid #0E0E14',
                        color: '#0E0E14',
                        '&:hover': {
                          backgroundColor: '#EFEFF0',
                        },
                      },
                      '&.MuiPaginationItem-root': {
                        border: '1px solid transparent',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      },
                    }}
                  />
                )}
              />
              <Typography variant="body2" color="#575E67">
                Showing {paginatedTVs.length} of {filteredTVs.length} results
              </Typography>
            </Box>
          </TableContainer>


          {/* Actions Menu */}
          <Menu
            anchorEl={actionMenuAnchor}
            open={Boolean(actionMenuAnchor)}
            onClose={handleActionClose}
          >
            <MenuItem
              onClick={() => {
                handleRefresh(selectedTV);
                handleActionClose();
              }}
            >
              <RiRefreshLine size={18} style={{ marginRight: 8 }} />
              Reload Screen
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClearCache(selectedTV);
                handleActionClose();
              }}
            >
              <Box
                component="img"
                src={ClearCache}
                alt="Clear Cache"
                sx={{
                  width: 18,
                  maxWidth: 18,
                  height: 'auto',
                  objectFit: 'contain',
                  marginRight: 1
                }}
              />
              Clear Cache
            </MenuItem>
            {/* <MenuItem
              onClick={() => {
                handleRefresh(selectedTV?.id);
                handleActionClose();
              }}
            >
              <RiRefreshLine size={18} style={{ marginRight: 8 }} />
              Refresh
            </MenuItem> */}
            <MenuItem
              onClick={() => {
                handleDeleteTV(selectedTV);
                handleActionClose();
              }}
              sx={{ color: colors.error.main }}
            >
              <RiDeleteBinLine size={18} style={{ marginRight: 8 }} />
              Delete
            </MenuItem>
          </Menu>
        </Box>
      )}

      <PairingCodeModal
        open={pairingModalOpen}
        onClose={() => {
          setPairingModalOpen(false);
          setGeneratedCode(null);
        }}
        onSubmit={handlePairTV}
        pairingCode={generatedCode}
      />
    </Box>
  );
};

export default TVManagement;

